import { Citation } from '@ucwip-frontend/ui-patterns';
import {
  AiAssistResponse,
  AiQuickLaunchAgent,
  CallHistoryEntry,
  IncomingCallInfo,
  QuickContact,
  RecommendedAction,
  TransferTarget,
  TranscriptMessage,
} from './active-call-console';

/**
 * Placeholder data — there's no telephony or transcription backend yet
 * (consistent with every screen built so far). Fixture names mirror the
 * prototype's Rivera Family / Rosa Mendez scenario (CCWIS_Agent_Demo_v6.html
 * occ* handlers) so the reconciled B1 stays recognizable against it.
 */
export const INCOMING_CALL_FIXTURE: IncomingCallInfo = {
  callId: 'CALL-DEMO-001',
  name: 'Rosa Mendez',
  caseId: 'CS-2847',
  phone: '(703) 555-0123',
  location: 'Richmond, VA',
  at: '2026-08-24T12:30:00-04:00',
  direction: 'inbound',
  initials: 'RM',
  matched: true,
};

export const UNMATCHED_CALL_FIXTURE: IncomingCallInfo = {
  callId: 'CALL-DEMO-002',
  name: 'Unknown caller',
  caseId: null,
  phone: '(571) 555-0199',
  at: '2026-08-24T12:35:00-04:00',
  direction: 'inbound',
  initials: '?',
  matched: false,
};

export const FIXTURE_QUICK_CONTACTS: QuickContact[] = [
  {
    id: 'CT-1',
    name: 'Rosa Mendez',
    role: 'Mother',
    phone: '(703) 555-0123',
    initials: 'RM',
  },
  {
    id: 'CT-2',
    name: 'Patricia Williams',
    role: 'Kinship Caregiver',
    phone: '(703) 555-0284',
    initials: 'PW',
  },
  {
    id: 'CT-3',
    name: 'Dr. Angela Lee',
    role: 'Therapist / LCSW',
    phone: '(703) 555-0312',
    initials: 'AL',
  },
  {
    id: 'CT-4',
    name: 'Ms. Patterson',
    role: 'School — Riverside Elem.',
    phone: '(703) 555-0144',
    initials: 'MP',
  },
];

export const FIXTURE_TRANSCRIPT: TranscriptMessage[] = [
  {
    id: 'MSG-1',
    speaker: 'caller',
    speakerLabel: 'Rosa',
    text: 'Hi, this is Rosa Mendez. I wanted to check on what I need to bring to the court hearing on December 5th.',
  },
  {
    id: 'MSG-2',
    speaker: 'worker',
    speakerLabel: 'Worker',
    text: 'Hi Rosa, thanks for calling. Let me pull up the case record right now.',
  },
  {
    id: 'MSG-3',
    speaker: 'caller',
    speakerLabel: 'Rosa',
    text: 'I completed all four parenting classes. I have the certificates. Do I need to bring those?',
  },
  {
    id: 'MSG-4',
    speaker: 'worker',
    speakerLabel: 'Worker',
    text: 'Yes, absolutely bring those — the judge will want to see proof of completion.',
  },
  {
    id: 'MSG-5',
    speaker: 'caller',
    speakerLabel: 'Rosa',
    text: 'And the children — can I bring them, or should they stay with Patricia?',
  },
];

export const FIXTURE_RECOMMENDED_ACTIONS: RecommendedAction[] = [
  {
    id: 'ACT-1',
    description: 'Log phone contact — Rosa asking about Dec 5 hearing requirements',
    category: 'Task',
    // Perform-only: parent handlers / future contact-log surface consume actionTriggered.
  },
  {
    id: 'ACT-2',
    description: 'Open court prep — Dec 5 hearing package, document checklist',
    category: 'Court',
    routeCommands: ['/cases', 'CS-2847', 'court'],
  },
  {
    id: 'ACT-3',
    description: 'Verify: Rosa says parenting classes complete (4/4) — confirm in case record',
    category: 'Verify',
    routeCommands: ['/cases', 'CS-2847', 'overview'],
  },
];

export const FIXTURE_AI_QUICK_LAUNCH_AGENTS: AiQuickLaunchAgent[] = [
  {
    id: 'summary',
    name: 'Case Summary',
    description: 'AI-generated status brief',
    iconGlyph: 'document',
  },
  {
    id: 'safety',
    name: 'Safety & Risk',
    description: 'SDM scoring & risk flags',
    iconGlyph: 'warning--alt',
  },
  {
    id: 'policy',
    name: 'Policy Lookup',
    description: '45 CFR, CFSR, FFPSA',
    iconGlyph: 'policy',
  },
  {
    id: 'court',
    name: 'Court Prep',
    description: 'Hearing package & checklist',
    iconGlyph: 'scales',
  },
  {
    id: 'permanency',
    name: 'Permanency',
    description: 'Reunification probability',
    iconGlyph: 'trophy',
  },
  {
    id: 'placement',
    name: 'Placement Match',
    description: 'Provider availability',
    iconGlyph: 'home',
  },
];

const CFR_CITATION: Citation = {
  label: '45 CFR §1356.21(h)',
  sourceTitle: 'Code of Federal Regulations, Title 45',
  sectionRef: '§1356.21(h)',
  quote:
    'The agency must conduct an administrative review of the case of each child in foster care no less frequently than once every six months.',
};

const SDM_MANUAL_CITATION: Citation = {
  label: 'VA SDM Manual p.18',
  sourceTitle: 'Virginia SDM Policy & Procedures Manual',
  sectionRef: 'p. 18',
  quote:
    'Risk re-assessment following a positive service update may move a case from HIGH to MODERATE risk.',
};

export const AI_ASSIST_RESPONSES: Record<string, AiAssistResponse> = {
  summary: {
    summary:
      'Case CS-2847 — Rivera Family: active ongoing case. Children Sophia (7) and Mateo (4) in kinship placement with Patricia Williams. Mother Rosa Mendez engaged in services; FSP goals 78% complete. Next court date Dec 5.',
    citations: [SDM_MANUAL_CITATION],
  },
  safety: {
    summary:
      'Current safety assessment: MODERATE risk. SDM score improved from HIGH (Nov) to MODERATE (Dec). Protective factors: stable kinship placement, Rosa engaged in services, no incidents since October.',
    citations: [SDM_MANUAL_CITATION],
  },
  policy: {
    summary:
      'Per 45 CFR §1356.21(h): the agency must conduct an administrative review of the case of each child in foster care no less frequently than once every six months.',
    citations: [CFR_CITATION],
  },
  court: {
    summary:
      'Dec 5 hearing prep — required documents: court report (submitted), FSP with signatures (Rosa signature pending), parenting class certificates, drug test results (negative). Missing: Rosa FSP signature.',
    citations: [CFR_CITATION],
  },
  permanency: {
    summary:
      'Permanency analysis for CS-2847: current goal is reunification. AI probability 89% based on 8 protective factors, 2 risk factors. Barriers remaining: housing assistance finalization, employment verification.',
    citations: [SDM_MANUAL_CITATION],
  },
  placement: {
    summary:
      'Current placement: Patricia Williams — licensed kinship, capacity 3, current 2. All clearances current through Apr 2026. Sibling group maintained. No alternative placement needed at this time.',
    citations: [SDM_MANUAL_CITATION],
  },
};

export const DEFAULT_AI_ASSIST_RESPONSE: AiAssistResponse = {
  summary:
    'I can help with case summaries, safety assessments, policy guidance, court preparation, permanency planning, and placement matching. Ask a specific question about this case or policy.',
  citations: [CFR_CITATION],
};

export const FIXTURE_TRANSFER_TARGETS: TransferTarget[] = [
  { id: 'SUP-1', name: 'Robert Hernandez', role: 'Supervisor' },
  { id: 'INV-1', name: 'James Okafor', role: 'Investigator' },
  { id: 'Q-1', name: 'Intake Queue', role: 'General hotline queue' },
];

export const FIXTURE_CALL_HISTORY: CallHistoryEntry[] = [
  {
    id: 'HIST-1',
    name: 'Rosa Mendez',
    direction: 'inbound',
    durationLabel: '8:32',
    timestampLabel: 'Today 2:14 PM · CS-2847',
    summary:
      'Asked about Dec 5 hearing requirements. Confirmed parenting class completion (4/4).',
  },
  {
    id: 'HIST-2',
    name: 'Patricia Williams',
    direction: 'outbound',
    durationLabel: '4:18',
    timestampLabel: 'Today 10:05 AM · CS-2847',
    summary:
      'Checked on children. Both settled, no concerns. Sophia doing well at school.',
  },
  {
    id: 'HIST-3',
    name: 'Dr. Angela Lee',
    direction: 'outbound',
    durationLabel: '12:44',
    timestampLabel: 'Nov 28 3:30 PM · CS-2847',
    summary:
      'Sophia session update. Trauma therapy progressing well, 6/10 sessions complete.',
  },
];
